import React, {useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useQuery} from "react-query";
import ngage from "services/ngage";
import {SponsorshipTier} from "types/SponsorshipTier";
import Button from "components/Button";
import {PencilIcon, TrashIcon} from "@heroicons/react/solid";
import Page from "components/Page";
import Card from "components/Card";
import DescriptionList from "components/DescriptionList";
import DescriptionListItem from "components/DescriptionListItem";
import DescriptionTerm from "components/DescriptionTerm";
import DescriptionDetails from "components/DescriptionDetails";
import {formattedDate} from "services/date";
import DeleteSponsorshipTier from "pages/SponsorshipTier/Delete";
import StatBox from "components/StatBox";
import {IdentificationIcon} from "@heroicons/react/outline";
import StatList from "components/StatList";
import {useOperator} from "context/OperatorContext";

function ViewSponsorshipTier() {
    const {id} = useParams()
    const {data: sponsorshipTier, isLoading} = useQuery(
        ['sponsorship-tier', {id}],
        () => ngage.sponsorshipTiers.get(parseInt(id as string))
    )

    const [deletingSponsorshipTier, setDeletingSponsorshipTier] = useState<SponsorshipTier | null>(null);

    const navigate = useNavigate()
    const {termFor} = useOperator()

    // Prepare action handlers
    function handleDeleteSponsorshipTier() {
        if(sponsorshipTier) setDeletingSponsorshipTier(sponsorshipTier)
    }

    function handleEditSponsorshipTier() {
        if(id) navigate(`/sponsorship-tiers/${id}/edit`)
    }

    // Prepare page actions
    const actions = <>
        <Button color={Button.colors.WHITE} icon={TrashIcon} onClick={handleDeleteSponsorshipTier} disabled={!sponsorshipTier || sponsorshipTier.sponsorship_count > 0}>Delete</Button>
        <Button color={Button.colors.PRIMARY} icon={PencilIcon} onClick={handleEditSponsorshipTier}>Edit</Button>
    </>

    // Prepare breadcrumbs
    const breadcrumbs = {
        "Audience": "/",
        [`${termFor('Sponsorship')} Tiers`]: "/sponsorship-tiers",
        [sponsorshipTier?.name || `View ${termFor('Sponsorship')} Tier`]: `/sponsorship-tiers/${id}`
    }

    return (
        <Page
            title={sponsorshipTier?.name || `View ${termFor('Sponsorship')} Tier`}
            breadcrumbs={breadcrumbs}
            actions={actions}
            isLoading={isLoading}
        >
            {!!sponsorshipTier &&
                <>
                    <section>
                        <Card
                            heading={`${termFor('Sponsorship')} Tier Information`}
                            subheading={`A tier used for ${sponsorshipTier.sponsorship_count} ${termFor('sponsorships')}`}
                        >
                            <DescriptionList>
                                <DescriptionListItem span={2}>
                                    <DescriptionTerm>Description</DescriptionTerm>
                                    <DescriptionDetails>
                                        <p className="whitespace-pre-line">
                                            {sponsorshipTier.description ?? `A ${sponsorshipTier.duration.toHuman()} tier`}
                                        </p>
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Duration</DescriptionTerm>
                                    <DescriptionDetails>
                                        {sponsorshipTier.duration.toHuman()}
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Price</DescriptionTerm>
                                    <DescriptionDetails>
                                        {sponsorshipTier.price? sponsorshipTier.price_formatted : 'Free'}
                                    </DescriptionDetails>
                                </DescriptionListItem>

                                {sponsorshipTier.symbol &&
                                    <DescriptionListItem span={1}>
                                        <DescriptionTerm>Symbol</DescriptionTerm>
                                        <DescriptionDetails>
                                            <img src={sponsorshipTier.symbol.icon_url} alt={`${sponsorshipTier.name} tier symbol`} className="h-12 w-12"/>
                                        </DescriptionDetails>
                                    </DescriptionListItem>
                                }

                                {sponsorshipTier.colour &&
                                    <DescriptionListItem span={1}>
                                        <DescriptionTerm>Colour</DescriptionTerm>
                                        <DescriptionDetails>
                                            <div style={{backgroundColor: sponsorshipTier.colour}} className="h-12 w-12 rounded shadow border border-gray-400"/>
                                        </DescriptionDetails>
                                    </DescriptionListItem>
                                }

                                <DescriptionListItem span={1}>
                                    <DescriptionTerm>Created</DescriptionTerm>
                                    <DescriptionDetails>
                                        {formattedDate(sponsorshipTier.created_at)}
                                    </DescriptionDetails>
                                </DescriptionListItem>
                            </DescriptionList>
                        </Card>
                    </section>

                    <section className="mt-4">
                        <StatList>
                            <StatBox
                                name={termFor('Sponsorships')}
                                icon={IdentificationIcon}
                                stat={sponsorshipTier.sponsorship_count}
                            />
                            {/*<StatBox name="Members" icon={UserIcon} stat={sponsorshipTier.member_count} />*/}
                            {/*<StatBox name="Renewal Rate" icon={PresentationChartLineIcon} stat={"62.6%"} />*/}
                        </StatList>
                    </section>
                </>
            }

            {/* Delete confirmation modal */}
            <DeleteSponsorshipTier
                sponsorshipTier={deletingSponsorshipTier}
                show={sponsorshipTier === deletingSponsorshipTier}
                onCancel={() => setDeletingSponsorshipTier(null)}
                onDelete={() => navigate('/sponsorship-tiers')}
                onDeleteFail={() => setDeletingSponsorshipTier(null)}
            />
        </Page>
    );
}

export default ViewSponsorshipTier;